import { useState } from 'react';
import { Box, Chip, Skeleton, Tab, Tabs, Typography, alpha, useTheme } from '@mui/material';
import { SupportedProvider, SupportedTab } from '../utils/types';
import TelephonyApiKeyIcon from '@/assets/icons/TelephonyApiKeyIcon';
import TelephonyMappingIcon from '@/assets/icons/TelephonyMappingIcon';
import Button from 'components/Common/Button';
import DocumentationDrawer from 'components/Common/DocumentationDrawer';

interface Props {
  provider: SupportedProvider;
  tab: SupportedTab;
  businessCount: number;
  loadingBusinessCount: boolean;
  articleId?: string | number;
  description?: string;
  onTabChange: (tab: SupportedTab) => void;
}

const getProviderDescription = (provider: SupportedProvider, apiDescription?: string) => {
  if (apiDescription) return apiDescription;

  if (provider === 'twilio') {
    return "It helps streamline call routing, messaging, and real-time communication, enhancing efficiency and responsiveness. Numbers are managed in each business's settings.";
  }
  if (provider === 'netsapiens') {
    return 'It helps streamline call routing, messaging, and real-time communication, enhancing efficiency and responsiveness.';
  }
  if (provider === 'asterisk') {
    return 'Connect your Asterisk PBX to configure and manage AI agents with SIP trunking through your platform.';
  }
  if (provider === 'bandwidth') {
    return 'It provides enterprise-grade voice and messaging APIs, enabling reliable call delivery and number management at scale.';
  }
  return 'It helps streamline call routing, messaging, and real-time communication, enhancing efficiency and responsiveness.';
};

const getProviderName = (provider: SupportedProvider) => {
  if (provider === 'netsapiens') return 'Netsapiens';
  if (provider === 'asterisk') return 'Asterisk';
  if (provider === '3cx') return '3CX';
  if (provider === 'bandwidth') return 'Bandwidth';
  return provider.charAt(0).toUpperCase() + provider.slice(1);
};

export default function TelephonyTopBar({
  provider,
  tab,
  businessCount,
  loadingBusinessCount,
  articleId,
  description,
  onTabChange
}: Props) {
  const theme = useTheme();
  const [docsDrawerOpen, setDocsDrawerOpen] = useState(false);
  const providerName = getProviderName(provider);
  const configTabLabel = provider === 'bandwidth' || provider === 'twilio' ? 'Configuration' : 'API Key';

  return (
    <>
      <Box display='flex' justifyContent='space-between' alignItems='center' gap={2}>
        <Box display='flex' gap={1} alignItems='center'>
          <Typography fontWeight={500} fontSize={20}>
            {providerName}
          </Typography>
          {loadingBusinessCount ? (
            <Skeleton
              variant='rounded'
              width={120}
              height={23}
              sx={{
                borderRadius: '999px'
              }}
            />
          ) : (
            <Chip
              label={`${businessCount} Businesses ${
                provider === 'asterisk' || provider === 'netsapiens' ? 'Mapped' : 'Active'
              }`}
              size='small'
              sx={{
                color: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                fontWeight: 500,
                borderRadius: '999px'
              }}
            />
          )}
        </Box>
        <Button
          variant='outlined'
          sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
          disabled={!articleId}
          onClick={() => setDocsDrawerOpen(true)}
        >
          View Documentation
        </Button>
      </Box>

      <Typography color='secondary' mt={0.5} mb={2} fontSize={14}>
        {getProviderDescription(provider, description)}
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, value) => onTabChange(value)}
        sx={{
          width: '100%',
          display: 'flex',
          mb: 2,
          backgroundColor: '#fff',
          borderRadius: '4px',
          border: '1px solid #F0F0F0',
          minHeight: 44,
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTabs-scroller': { width: '100%' },
          '& .MuiTabs-flexContainer': { width: '100%' },
          '& .MuiTab-root': {
            minHeight: 44,
            flex: 1,
            maxWidth: 'none',
            textTransform: 'none',
            fontWeight: 500,
            color: '#777',
            display: 'flex',
            flexDirection: 'row',
            gap: '6px'
          },
          '& .Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.2),
            color: theme.palette.text.primary
          }
        }}
      >
        <Tab icon={<TelephonyApiKeyIcon />} iconPosition='start' label={configTabLabel} value='api-key' />
        <Tab icon={<TelephonyMappingIcon />} iconPosition='start' label='Mapping' value='mapping' />
      </Tabs>

      <DocumentationDrawer
        open={docsDrawerOpen}
        onClose={() => setDocsDrawerOpen(false)}
        articleId={articleId ?? null}
        fallbackTitle={`${providerName} Documentation`}
      />
    </>
  );
}
